import { Box, Button, Grid, Typography } from '@mui/material'
import { Page } from 'components/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { SectionHeader } from 'components/SectionHeader'
import { SimpleImageCard } from 'components/ImageCard/SimpleImageCard'
import { useGetLocationBadgeById } from 'hooks/api/Badges/useGetLocationBadgeById'
import { useParams } from 'react-router-dom'
import {
  type BadgeRewardRenewableType,
  type BadgeRewardType,
  type BadgeSubtype,
} from 'src/types/api'
import { formatDateTime } from 'utils/util'
import { useGetPatronBadgeCountsByBadgeId } from 'hooks/api/Badges/useGetPatronBadgeCountsByBadgeId'
import { BadgeLocationsModal } from './BadgeLocationsModal'
import { useState } from 'react'
import { useGetSweepstakesByTypeWithGrouping } from 'hooks/api/useGetSweepstakesByTypeWithGrouping'
import { useGetCorporateAccountById } from 'hooks/api/useGetCorporateAccountById'
import { useGetOrganizationById } from 'hooks/api/useGetOrganizationById'
import { useGetRewardProgramById } from 'hooks/api/useGetRewardProgramById'

const getBadgeRewardTypeName = (type?: BadgeRewardType) => {
  switch (type) {
    case 'CheckIn':
      return 'Check-In'
    case 'CheckInStreak':
      return 'Check-In Streak'
    case 'PatronProfile_Address':
      return 'Patron Profile - Address'
    case 'PatronProfile_Anniversary':
      return 'Patron Profile - Anniversary'
    case 'PatronProfile_Birthday':
      return 'Patron Profile - Birthday'
    case 'Referral_Count':
      return 'Referral - Count'
    case 'LocationCheckIn':
      return 'Location Check-In'
    case 'LocationCheckInStreak':
      return 'Location Check-In Streak'
    case 'LocationUniqueCheckIn':
      return 'Unique Location Check-In'
    case 'UniqueLocationVisits':
      return 'Unique Location Visits'
    default:
      return '-'
  }
}

const getBadgeSubtypeName = (subtype?: BadgeSubtype | null) => {
  switch (subtype) {
    case 'CorporateAccount':
      return 'Corporate Account'
    case 'Organization':
      return 'Organization'
    case 'Location':
      return 'Location'
    default:
      return '-'
  }
}

const getRenewableTypeName = (renewableType?: BadgeRewardRenewableType) => {
  switch (renewableType) {
    case 'Daily':
      return 'Daily'
    case 'Weekly':
      return 'Weekly'
    case 'Monthly':
      return 'Monthly'
    case 'Quarterly':
      return 'Quarterly'
    case 'Yearly':
      return 'Yearly'
    case 'CheckInStreak':
      return 'Check-In Streak'
    case 'LocationCheckInStreak':
      return 'Location Check-In Streak'
    default:
      return 'No'
  }
}

interface SweepstakeView {
  id: number
  name: string
  groupId?: string | null
}

const formatOptionalNumber = (value: number | null | undefined) =>
  value === null || value === undefined || value === 0 ? '-' : value

const DetailField = ({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="body2" mb={1} color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body-1" mb={2} className="break-words">
      {value ?? '-'}
    </Typography>
  </Grid>
)

const ImageSection = ({
  imageSource,
  title,
}: {
  imageSource?: string | null
  title: string
}) => (
  <Box>
    <SectionHeader title={title} showButton={false} />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        rowGap: '16px',
        borderWidth: 1,
        borderColor: 'grey.300',
        borderRadius: 2,
        px: '16px',
        py: '16px',
        mt: '12px',
      }}
    >
      <Grid container>
        <Grid item xs={12} sm={12} md={12} sx={{ pt: { xs: '24px', sm: 0 } }}>
          <Typography variant="body-3" className="w-full">
            Thumbnail Image
          </Typography>
          <SimpleImageCard
            imageSource={imageSource}
            sx={{ maxWidth: '358px', maxHeight: '358px' }}
          />
        </Grid>
      </Grid>
    </Box>
  </Box>
)

const BadgeReportsSection = ({
  totalCount,
  uniqueCount,
  totalLocationPointsCount,
  totalLocationSweepstakeEntriesCount,
}: {
  totalCount?: number
  uniqueCount?: number
  totalLocationPointsCount?: number
  totalLocationSweepstakeEntriesCount?: number
}) => (
  <Box className="pt-4">
    <SectionHeader title="Badge Reports" showButton={false} />
    <Box
      border={{ md: '1px solid' }}
      borderColor={{ md: 'grey.200' }}
      borderRadius="5px"
      p={{ xs: 0, sm: 3 }}
    >
      <Grid container className="flex flex-row flex-wrap" columnGap={15}>
        <Grid item md={2}>
          <Typography variant="body2" pb={1} color="text.secondary">
            Total Count
          </Typography>
          <Typography variant="body1" pb={2}>
            {totalCount}
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant="body2" pb={1} color="text.secondary">
            Unique Count
          </Typography>
          <Typography variant="body1" pb={2}>
            {uniqueCount}
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant="body2" pb={1} color="text.secondary">
            Total Location Points
          </Typography>
          <Typography variant="body1" pb={2}>
            {totalLocationPointsCount}
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant="body2" pb={1} color="text.secondary">
            Total Location Sweepstake Entries
          </Typography>
          <Typography variant="body1" pb={2}>
            {totalLocationSweepstakeEntriesCount}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  </Box>
)

const hasBadgeCounts = (
  value: unknown
): value is {
  totalCount?: number
  uniqueCount?: number
  totalLocationPointsCount?: number
  totalLocationSweepstakeEntriesCount?: number
} => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'totalCount' in value &&
    'uniqueCount' in value &&
    'totalLocationPointsCount' in value &&
    'totalLocationSweepstakeEntriesCount' in value
  )
}

export const LocationBadgeDetailsPage = () => {
  const badgeRewardId = Number(useParams().id)
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState(false)
  const badgeRewardQuery = useGetLocationBadgeById(badgeRewardId)
  const badgeReward = badgeRewardQuery.data
  const getPatronBadgeCountQuery = useGetPatronBadgeCountsByBadgeId({
    id: badgeRewardId,
  })
  const badgeCountData = getPatronBadgeCountQuery.data
  const totalCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalCount
    : undefined
  const uniqueCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.uniqueCount
    : undefined
  const totalLocationPointsCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalLocationPointsCount
    : undefined
  const totalLocationSweepstakeEntriesCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalLocationSweepstakeEntriesCount
    : undefined

  const rewardProgramId = badgeRewardQuery.data?.rewardsProgramId?.toString()
  const rewardProgramQuery = useGetRewardProgramById({
    rewardProgramId,
  })

  const corporateAccountId = badgeRewardQuery.data?.corporateAccountId ?? 0
  const corporateAccountQuery = useGetCorporateAccountById({
    corporateAccountId,
    enabled:
      badgeRewardQuery.data?.subtype === 'CorporateAccount' &&
      corporateAccountId > 0,
  })

  const organizationId = badgeRewardQuery.data?.organizationId ?? 0
  const organizationQuery = useGetOrganizationById({ organizationId })

  const isSweepstake = (value: unknown): value is SweepstakeView => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'id' in value &&
      'name' in value
    )
  }

  const locationSweepstakesQuery = useGetSweepstakesByTypeWithGrouping({
    type: 'LocationSweepstake',
  })

  const locationSweepstakeName = (() => {
    const allLocationSweepstakesData: unknown = locationSweepstakesQuery.data
    const locationSweepstakes = Array.isArray(allLocationSweepstakesData)
      ? allLocationSweepstakesData.filter(isSweepstake)
      : []

    if (badgeReward?.locationSweepstakeContestId) {
      return (
        locationSweepstakes.find(
          (sweepstake) =>
            sweepstake.id === badgeReward.locationSweepstakeContestId
        )?.name ?? ''
      )
    }

    if (badgeReward?.locationSweepstakeGroupId) {
      return (
        locationSweepstakes.find(
          (sweepstake) =>
            sweepstake.groupId === badgeReward?.locationSweepstakeGroupId
        )?.name ?? ''
      )
    }

    return ''
  })()

  if (badgeRewardQuery.isPending) {
    return <ActivityIndicator />
  }

  if (badgeRewardQuery.isError || !badgeReward?.id) {
    return <Typography>Badge reward not found</Typography>
  }

  return (
    <Page
      header={
        <PageHeader
          title={badgeReward.name}
          identifier={badgeReward.id.toString()}
          backText="Location Badges"
          backPath="/LocationBadges"
          isSecondary={true}
        />
      }
    >
      <Box className="gap-12 flex flex-col">
        <Box>
          <SectionHeader title="Badge Details" showButton={false} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '40px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              p: '24px',
            }}
          >
            <Grid container columnSpacing={3} rowSpacing={2}>
              <DetailField
                label="Type"
                value={getBadgeRewardTypeName(badgeReward.type)}
              />
              <DetailField
                label="SubType"
                value={getBadgeSubtypeName(badgeReward.subtype)}
              />

              {badgeReward?.subtype === 'CorporateAccount' && (
                <Grid item md={3}>
                  <Typography variant="body2" mb={1} color="text.secondary">
                    Corporate Account
                  </Typography>
                  <Typography variant="body-1" mb={2}>
                    {corporateAccountQuery.data?.name ?? '-'}
                  </Typography>
                </Grid>
              )}
              {badgeReward?.subtype === 'Organization' && (
                <Grid item md={3}>
                  <Typography variant="body2" mb={1} color="text.secondary">
                    Organization
                  </Typography>
                  <Typography variant="body-1" mb={2}>
                    {organizationQuery.data?.name ?? '-'}
                  </Typography>
                </Grid>
              )}
              {badgeReward?.subtype === 'Location' && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" mb={1} color="text.secondary">
                    Licensed Establishments
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      onClick={() => setIsLocationsModalOpen(true)}
                    >
                      View Locations
                    </Button>
                    <Typography variant="body-1">
                      {`${
                        badgeReward.licensedEstablishmentIds?.length ??
                        badgeReward.licensedEstablishments?.length ??
                        0
                      } location(s) selected`}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <DetailField
                label="Description"
                value={badgeReward.description || '-'}
              />
              <DetailField
                label="Start Date"
                value={
                  badgeReward.startDate
                    ? formatDateTime(badgeReward.startDate, 'P p')
                    : '-'
                }
              />
              <DetailField
                label="End Date"
                value={
                  badgeReward.endDate
                    ? formatDateTime(badgeReward.endDate, 'P p')
                    : '-'
                }
              />
              <DetailField
                label="Scheduled Days"
                value={
                  badgeReward.badgeRewardSchedules &&
                  badgeReward.badgeRewardSchedules.length > 0
                    ? 'Yes'
                    : 'No'
                }
              />
              <DetailField
                label={`${getBadgeRewardTypeName(badgeReward.type)} Count`}
                value={formatOptionalNumber(badgeReward.count)}
              />
              <DetailField
                label="Renewable"
                value={getRenewableTypeName(badgeReward.renewableType)}
              />
              <DetailField
                label="Depends On Badge Id"
                value={formatOptionalNumber(badgeReward.dependentBadgeRewardId)}
              />
              <DetailField
                label="Limit to 1 Check-in Per Day"
                value={badgeReward.limitCheckins ? 'Yes' : 'No'}
              />
              <DetailField
                label="Rewards Program"
                value={rewardProgramQuery.data?.name ?? '-'}
              />
              <DetailField
                label="Location points to award"
                value={formatOptionalNumber(badgeReward.locationPointsAwarded)}
              />
              <DetailField
                label="Location sweepstake entries to award"
                value={formatOptionalNumber(
                  badgeReward.locationSweepstakeEntriesAwarded
                )}
              />
              <DetailField
                label="Location sweepstake"
                value={
                  locationSweepstakeName.length > 0
                    ? locationSweepstakeName
                    : '-'
                }
              />
            </Grid>
          </Box>
        </Box>

        <BadgeReportsSection
          totalCount={totalCount}
          uniqueCount={uniqueCount}
          totalLocationPointsCount={totalLocationPointsCount}
          totalLocationSweepstakeEntriesCount={
            totalLocationSweepstakeEntriesCount
          }
        />

        <ImageSection
          title="Not Earned Image"
          imageSource={badgeReward.imageNotEarned}
        />
        <ImageSection
          title="Earned Image"
          imageSource={badgeReward.imageEarned}
        />
      </Box>
      <BadgeLocationsModal
        isOpen={isLocationsModalOpen}
        licensedEstablishmentIds={
          badgeReward.licensedEstablishmentIds ??
          badgeReward.licensedEstablishments?.map(
            (licensedEstablishment) => licensedEstablishment.id
          ) ??
          []
        }
        onClose={() => setIsLocationsModalOpen(false)}
      />
    </Page>
  )
}
